import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Button } from './button'
import { Icons } from '@/theme/Images'

const Drawer = ({ isOpen, onClose, children, title = 'Drawer' }) => {
  const [isVisible, setIsVisible] = useState(false)
  const [shouldRender, setShouldRender] = useState(false)

  // Handle open/close animation
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true)
      // Reset visibility state and then animate in
      setIsVisible(false)
      // Use a small timeout to ensure the DOM is ready and CSS transitions work
      const timer = setTimeout(() => {
        setIsVisible(true)
      }, 16) // ~1 frame at 60fps
      return () => clearTimeout(timer)
    } else {
      setIsVisible(false)
      // Delay unmounting to allow slide-out animation to complete
      const timer = setTimeout(() => setShouldRender(false), 300)
      return () => clearTimeout(timer)
    }
  }, [isOpen])

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }

    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  // Handle escape key
  useEffect(() => {
    const handleEscape = e => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, onClose])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [])

  if (!shouldRender) return null

  return createPortal(
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div
        aria-hidden="true"
        className={`fixed inset-0 bg-black/50 transition-opacity duration-300 will-change-opacity ${
          isVisible ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`fixed right-0 top-0 h-full w-[484px] bg-white shadow-xl transform transition-transform duration-300 ease-in-out will-change-transform ${
          isVisible ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            <Button variant="ghost" size="sm" onClick={onClose} className="p-2 hover:bg-gray-100">
              <img src={Icons.closeIcon} alt="close" className="w-4 h-4" />
            </Button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">{children}</div>
        </div>
      </div>
    </div>,
    document.body
  )
}

export { Drawer }
